/**
 * Location Alerts Page
 *
 * Shows location violation alerts for administrators to review.
 */

import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from './auth-context'
import * as alertsApi from './api/alerts'
import './App.css'

function LocationAlerts() {
  const { user, currentOrg, hasPermission, userRole } = useAuth()

  const [alerts, setAlerts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [statusFilter, setStatusFilter] = useState('pending')
  const [updating, setUpdating] = useState(null)

  // Check permissions
  const canViewAlerts =
    hasPermission('alerts_view') || userRole === 'owner' || userRole === 'administrator'

  // Load alerts
  useEffect(() => {
    if (!user || !currentOrg || !canViewAlerts) return

    let cancelled = false
    setLoading(true)
    ;(async () => {
      try {
        const result = await alertsApi.listAlerts(currentOrg.id, {
          status: statusFilter === 'all' ? null : statusFilter,
          limit: 100,
        })
        if (cancelled) return
        setAlerts(result.items || [])
      } catch (e) {
        if (!cancelled) {
          setError('Failed to load alerts')
          console.error('Failed to load alerts:', e)
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [user, currentOrg, statusFilter, canViewAlerts])

  // Handle status update
  const handleUpdateStatus = async (alertId, newStatus, notes = null) => {
    setUpdating(alertId)
    setError('')
    setSuccess('')

    try {
      await alertsApi.updateAlertStatus(currentOrg.id, alertId, {
        status: newStatus,
        notes: notes,
      })

      setSuccess(`Alert marked as ${newStatus}`)

      // Reload alerts
      const result = await alertsApi.listAlerts(currentOrg.id, {
        status: statusFilter === 'all' ? null : statusFilter,
        limit: 100,
      })
      setAlerts(result.items || [])
    } catch (e) {
      console.error('Failed to update alert:', e)
      setError(e.message || 'Failed to update alert')
    } finally {
      setUpdating(null)
    }
  }

  // Format distance
  const formatDistance = (meters) => {
    if (meters >= 1000) {
      return `${(meters / 1000).toFixed(1)} km`
    }
    return `${Math.round(meters)} m`
  }

  // Format date
  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  // Get status badge
  const getStatusBadge = (status) => {
    const styles = {
      pending: { bg: '#fef3c7', color: '#92400e', label: 'Pending' },
      reviewed: { bg: '#dcfce7', color: '#166534', label: 'Reviewed' },
      dismissed: { bg: '#f3f4f6', color: '#4b5563', label: 'Dismissed' },
    }
    const style = styles[status] || styles.pending
    return (
      <span
        style={{
          display: 'inline-block',
          padding: '0.25rem 0.5rem',
          background: style.bg,
          color: style.color,
          borderRadius: '10px',
          fontSize: '0.75rem',
          fontWeight: '600',
        }}>
        {style.label}
      </span>
    )
  }

  if (!canViewAlerts) {
    return (
      <div className="alerts-page">
        <div className="container">
          <div className="error-state">
            <p>You don't have permission to view alerts.</p>
            <Link to="/dashboard">Back to Dashboard</Link>
          </div>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="alerts-page">
        <div className="container">
          <div className="loading-state">
            <div className="loading-spinner">
              <div className="spinner-ring"></div>
            </div>
            <p>Loading alerts...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="alerts-page">
      <div className="container" style={{ maxWidth: '1100px', padding: '2rem' }}>
        {/* Header */}
        <div className="header" style={{ marginBottom: '2rem' }}>
          <Link to="/dashboard" style={{ color: '#0D9488', textDecoration: 'none' }}>
            ← Back to Dashboard
          </Link>
          <h1 style={{ margin: '1rem 0 0.5rem 0' }}>Location Alerts</h1>
          <p style={{ color: '#666', margin: 0 }}>
            Review responses submitted outside assigned geo-fence locations.
          </p>
        </div>

        {/* Messages */}
        {error && (
          <div
            style={{
              background: '#f8d7da',
              color: '#721c24',
              padding: '1rem',
              borderRadius: '8px',
              marginBottom: '1rem',
            }}>
            {error}
          </div>
        )}
        {success && (
          <div
            style={{
              background: '#d4edda',
              color: '#155724',
              padding: '1rem',
              borderRadius: '8px',
              marginBottom: '1rem',
            }}>
            {success}
          </div>
        )}

        {/* Filters */}
        <div
          style={{
            background: '#f8f9fa',
            borderRadius: '12px',
            padding: '1rem',
            marginBottom: '2rem',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
          }}>
          <span style={{ fontWeight: '600' }}>Filter by status:</span>
          {['all', 'pending', 'reviewed', 'dismissed'].map((status) => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              style={{
                padding: '0.5rem 1rem',
                background: statusFilter === status ? '#0D9488' : 'white',
                color: statusFilter === status ? 'white' : '#333',
                border: '1px solid #dee2e6',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: statusFilter === status ? '600' : 'normal',
              }}>
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>

        {/* Alerts List */}
        <div
          style={{
            background: 'white',
            border: '1px solid #dee2e6',
            borderRadius: '12px',
            overflow: 'hidden',
          }}>
          {alerts.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
              <p>🎉 No {statusFilter !== 'all' ? statusFilter : ''} alerts!</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #dee2e6', background: '#f8f9fa' }}>
                    <th style={{ textAlign: 'left', padding: '1rem', fontWeight: '600' }}>
                      Field Agent
                    </th>
                    <th style={{ textAlign: 'left', padding: '1rem', fontWeight: '600' }}>
                      Location
                    </th>
                    <th style={{ textAlign: 'center', padding: '1rem', fontWeight: '600' }}>
                      Distance
                    </th>
                    <th style={{ textAlign: 'center', padding: '1rem', fontWeight: '600' }}>
                      Status
                    </th>
                    <th style={{ textAlign: 'left', padding: '1rem', fontWeight: '600' }}>Date</th>
                    <th style={{ textAlign: 'right', padding: '1rem', fontWeight: '600' }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {alerts.map((alert) => (
                    <tr key={alert.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                      <td style={{ padding: '1rem' }}>
                        <div style={{ fontWeight: '600' }}>
                          {alert.field_agent_name || 'Unknown'}
                        </div>
                        <div style={{ color: '#666', fontSize: '0.875rem' }}>
                          Survey: {alert.survey_title || alert.survey_id}
                        </div>
                      </td>
                      <td style={{ padding: '1rem' }}>
                        <div style={{ fontSize: '0.875rem' }}>
                          <span style={{ color: '#666' }}>Expected: </span>
                          {alert.expected_location?.name || '—'}
                        </div>
                        <div style={{ fontSize: '0.875rem' }}>
                          <span style={{ color: '#666' }}>Actual: </span>
                          {alert.actual_location
                            ? `${alert.actual_location.lat?.toFixed(4)}, ${alert.actual_location.lng?.toFixed(4)}`
                            : 'Unknown'}
                        </div>
                      </td>
                      <td style={{ padding: '1rem', textAlign: 'center' }}>
                        <div
                          style={{
                            display: 'inline-block',
                            padding: '0.375rem 0.75rem',
                            background: '#fee2e2',
                            color: '#dc2626',
                            borderRadius: '10px',
                            fontWeight: '600',
                          }}>
                          {formatDistance(alert.distance_meters)}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '0.25rem' }}>
                          Allowed: {formatDistance(alert.allowed_radius_meters)}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#dc2626' }}>
                          +{formatDistance(alert.exceeded_by_meters || 0)} over
                        </div>
                      </td>
                      <td style={{ padding: '1rem', textAlign: 'center' }}>
                        {getStatusBadge(alert.status)}
                      </td>
                      <td style={{ padding: '1rem' }}>
                        <div style={{ fontSize: '0.875rem' }}>{formatDate(alert.created_at)}</div>
                        {alert.reviewed_at && (
                          <div style={{ fontSize: '0.75rem', color: '#666' }}>
                            Reviewed: {formatDate(alert.reviewed_at)}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '1rem', textAlign: 'right' }}>
                        {alert.status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleUpdateStatus(alert.id, 'reviewed')}
                              disabled={updating === alert.id}
                              style={{
                                padding: '0.375rem 0.75rem',
                                background: '#28a745',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: updating === alert.id ? 'not-allowed' : 'pointer',
                                marginRight: '0.5rem',
                                fontSize: '0.875rem',
                                opacity: updating === alert.id ? 0.7 : 1,
                              }}>
                              ✓ Review
                            </button>
                            <button
                              onClick={() => handleUpdateStatus(alert.id, 'dismissed')}
                              disabled={updating === alert.id}
                              style={{
                                padding: '0.375rem 0.75rem',
                                background: '#6c757d',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: updating === alert.id ? 'not-allowed' : 'pointer',
                                fontSize: '0.875rem',
                                opacity: updating === alert.id ? 0.7 : 1,
                              }}>
                              Dismiss
                            </button>
                          </>
                        )}
                        {alert.status !== 'pending' && (
                          <button
                            onClick={() => handleUpdateStatus(alert.id, 'pending')}
                            disabled={updating === alert.id}
                            style={{
                              padding: '0.375rem 0.75rem',
                              background: '#ffc107',
                              color: '#000',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: updating === alert.id ? 'not-allowed' : 'pointer',
                              fontSize: '0.875rem',
                              opacity: updating === alert.id ? 0.7 : 1,
                            }}>
                            Reopen
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default LocationAlerts
